/**
 * Vehicle Settings Page - Admin
 * ==============================
 * صفحة إدارة فئات المركبات ومتطلباتها
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';

const I = {
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>,
  list: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
};

const fieldTypes = [
  { value: 'text', label: 'نص' },
  { value: 'number', label: 'رقم' },
  { value: 'number_ar', label: 'رقم عربي' },
  { value: 'letters_ar', label: 'حروف عربية' },
  { value: 'letters_en', label: 'حروف إنجليزية' },
  { value: 'select', label: 'قائمة اختيار' },
  { value: 'date', label: 'تاريخ' },
  { value: 'file', label: 'ملف' },
  { value: 'textarea', label: 'نص طويل' },
];

const groupOptions = [
  { value: 'basic_info', label: 'المعلومات الأساسية' },
  { value: 'plate_info', label: 'معلومات اللوحة' },
  { value: 'documents', label: 'أرقام الوثائق' },
  { value: 'document_files', label: 'ملفات الوثائق' },
];

const VehicleSettingsPage = () => {
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();

  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [categories, setCategories] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingRequirement, setEditingRequirement] = useState(null);
  
  // Forms
  const [categoryForm, setCategoryForm] = useState({ name: '', name_en: '', capacity_tons: '', description: '', sort_order: 0 });
  const [requirementForm, setRequirementForm] = useState({
    field_key: '', field_name_ar: '', field_name_en: '', field_type: 'text',
    is_required: false, validation_regex: '', validation_message: '', placeholder: '',
    default_value: '', options: '', group_name: '', sort_order: 0, needs_verification: false,
    file_types: '', max_file_size: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catsRes, reqsRes, statsRes] = await Promise.all([
        apiCall('/tenant/vehicle-settings/categories?include_inactive=true'),
        apiCall('/tenant/vehicle-settings/requirements?include_inactive=true'),
        apiCall('/tenant/vehicle-settings/stats')
      ]);
      setCategories(catsRes);
      setRequirements(reqsRes);
      setStats(statsRes);
    } catch (err) {
      showError('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  useEffect(() => { loadData(); }, [loadData]);

  // Categories CRUD
  const openCategoryModal = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({
        name: cat.name || '',
        name_en: cat.name_en || '',
        capacity_tons: cat.capacity_tons || '',
        description: cat.description || '',
        sort_order: cat.sort_order || 0
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', name_en: '', capacity_tons: '', description: '', sort_order: 0 });
    }
    setShowCategoryModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...categoryForm,
        capacity_tons: categoryForm.capacity_tons ? parseFloat(categoryForm.capacity_tons) : null
      };
      
      if (editingCategory) {
        await apiCall(`/tenant/vehicle-settings/categories/${editingCategory.id}`, { method: 'PUT', body: JSON.stringify(data) });
        success('تم تحديث الفئة');
      } else {
        await apiCall('/tenant/vehicle-settings/categories', { method: 'POST', body: JSON.stringify(data) });
        success('تم إضافة الفئة');
      }
      setShowCategoryModal(false);
      loadData();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    }
  };

  const deleteCategory = async (id) => {
    if (!await confirm({ title: 'حذف الفئة', message: 'هل تريد حذف هذه الفئة؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/vehicle-settings/categories/${id}`, { method: 'DELETE' });
      success('تم حذف الفئة');
      loadData();
    } catch (err) {
      showError('فشل الحذف');
    }
  };

  // Requirements CRUD
  const openRequirementModal = (req = null) => {
    if (req) {
      setEditingRequirement(req);
      setRequirementForm({
        field_key: req.field_key || '',
        field_name_ar: req.field_name_ar || '',
        field_name_en: req.field_name_en || '',
        field_type: req.field_type || 'text',
        is_required: req.is_required || false,
        validation_regex: req.validation_regex || '',
        validation_message: req.validation_message || '',
        placeholder: req.placeholder || '',
        default_value: req.default_value || '',
        options: req.options ? req.options.join(', ') : '',
        group_name: req.group_name || '',
        sort_order: req.sort_order || 0,
        needs_verification: req.needs_verification || false,
        file_types: req.file_types || '',
        max_file_size: req.max_file_size || ''
      });
    } else {
      setEditingRequirement(null);
      setRequirementForm({
        field_key: '', field_name_ar: '', field_name_en: '', field_type: 'text',
        is_required: false, validation_regex: '', validation_message: '', placeholder: '',
        default_value: '', options: '', group_name: '', sort_order: 0, needs_verification: false,
        file_types: '', max_file_size: ''
      });
    }
    setShowRequirementModal(true);
  };

  const handleRequirementSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...requirementForm,
        options: requirementForm.options ? requirementForm.options.split(',').map(o => o.trim()) : null,
        max_file_size: requirementForm.max_file_size ? parseInt(requirementForm.max_file_size) : null
      };
      
      if (editingRequirement) {
        await apiCall(`/tenant/vehicle-settings/requirements/${editingRequirement.id}`, { method: 'PUT', body: JSON.stringify(data) });
        success('تم تحديث المتطلب');
      } else {
        await apiCall('/tenant/vehicle-settings/requirements', { method: 'POST', body: JSON.stringify(data) });
        success('تم إضافة المتطلب');
      }
      setShowRequirementModal(false);
      loadData();
    } catch (err) {
      showError(err.message || 'فشلت العملية');
    }
  };

  const deleteRequirement = async (id) => {
    if (!await confirm({ title: 'حذف المتطلب', message: 'هل تريد حذف هذا المتطلب؟', type: 'delete', confirmText: 'حذف', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/vehicle-settings/requirements/${id}`, { method: 'DELETE' });
      success('تم حذف المتطلب');
      loadData();
    } catch (err) {
      showError('فشل الحذف');
    }
  };

  const toggleRequirementActive = async (req) => {
    try {
      await apiCall(`/tenant/vehicle-settings/requirements/${req.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !req.is_active })
      });
      loadData();
    } catch (err) {
      showError('فشل التحديث');
    }
  };

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  return (
    <div className="vehicle-settings-page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>إعدادات المركبات</h1>
          <p>إدارة فئات المركبات ومتطلباتها</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card">
            <span className="stat-value">{stats.total_categories}</span>
            <span className="stat-label">فئات المركبات</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.total_requirements}</span>
            <span className="stat-label">المتطلبات</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.required_requirements}</span>
            <span className="stat-label">إلزامية</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.optional_requirements}</span>
            <span className="stat-label">اختيارية</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => setActiveTab('categories')}>
          {I.truck} فئات المركبات
        </button>
        <button className={`tab ${activeTab === 'requirements' ? 'active' : ''}`} onClick={() => setActiveTab('requirements')}>
          {I.list} متطلبات التسجيل
        </button>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>فئات المركبات</h3>
            <button className="btn btn--primary btn--sm" onClick={() => openCategoryModal()}>{I.plus} إضافة فئة</button>
          </div>
          
          <div className="cards-grid">
            {categories.map(cat => (
              <div key={cat.id} className={`card ${!cat.is_active ? 'inactive' : ''}`}>
                <div className="card-header">
                  <h4>{cat.name}</h4>
                  {!cat.is_active && <span className="badge badge--danger">غير نشط</span>}
                </div>
                <div className="card-body">
                  {cat.capacity_tons && <p><strong>السعة:</strong> {cat.capacity_tons} طن</p>}
                  {cat.name_en && <p><strong>بالإنجليزية:</strong> {cat.name_en}</p>}
                  {cat.description && <p>{cat.description}</p>}
                </div>
                <div className="card-actions">
                  <button onClick={() => openCategoryModal(cat)}>{I.edit}</button>
                  <button onClick={() => deleteCategory(cat.id)} className="danger">{I.trash}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requirements Tab */}
      {activeTab === 'requirements' && (
        <div className="tab-content">
          <div className="section-header">
            <h3>متطلبات التسجيل</h3>
            <button className="btn btn--primary btn--sm" onClick={() => openRequirementModal()}>{I.plus} إضافة متطلب</button>
          </div>
          
          <div className="requirements-table">
            <table>
              <thead>
                <tr>
                  <th>الحقل</th>
                  <th>النوع</th>
                  <th>المجموعة</th>
                  <th>إلزامي</th>
                  <th>يحتاج مراجعة</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {requirements.map(req => (
                  <tr key={req.id} className={!req.is_active ? 'inactive' : ''}>
                    <td>
                      <strong>{req.field_name_ar}</strong>
                      <small>{req.field_key}</small>
                    </td>
                    <td>{fieldTypes.find(t => t.value === req.field_type)?.label || req.field_type}</td>
                    <td>{groupOptions.find(g => g.value === req.group_name)?.label || req.group_name || '-'}</td>
                    <td>{req.is_required ? <span className="badge badge--success">نعم</span> : <span className="badge badge--secondary">لا</span>}</td>
                    <td>{req.needs_verification ? <span className="badge badge--warning">نعم</span> : '-'}</td>
                    <td>
                      <button className={`toggle-btn ${req.is_active ? 'active' : ''}`} onClick={() => toggleRequirementActive(req)}>
                        {req.is_active ? 'نشط' : 'غير نشط'}
                      </button>
                    </td>
                    <td>
                      <div className="actions">
                        <button onClick={() => openRequirementModal(req)}>{I.edit}</button>
                        <button onClick={() => deleteRequirement(req.id)} className="danger">{I.trash}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'تعديل فئة' : 'إضافة فئة جديدة'}</h2>
              <button className="modal-close" onClick={() => setShowCategoryModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleCategorySubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>الاسم بالعربية *</label>
                  <input type="text" value={categoryForm.name} onChange={e => setCategoryForm({...categoryForm, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>الاسم بالإنجليزية</label>
                  <input type="text" value={categoryForm.name_en} onChange={e => setCategoryForm({...categoryForm, name_en: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>السعة (طن)</label>
                  <input type="number" step="0.1" value={categoryForm.capacity_tons} onChange={e => setCategoryForm({...categoryForm, capacity_tons: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>الوصف</label>
                  <textarea value={categoryForm.description} onChange={e => setCategoryForm({...categoryForm, description: e.target.value})} rows={2} />
                </div>
                <div className="form-group">
                  <label>الترتيب</label>
                  <input type="number" value={categoryForm.sort_order} onChange={e => setCategoryForm({...categoryForm, sort_order: parseInt(e.target.value) || 0})} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowCategoryModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">حفظ</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Requirement Modal */}
      {showRequirementModal && (
        <div className="modal-overlay" onClick={() => setShowRequirementModal(false)}>
          <div className="modal modal--lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRequirement ? 'تعديل متطلب' : 'إضافة متطلب جديد'}</h2>
              <button className="modal-close" onClick={() => setShowRequirementModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleRequirementSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>مفتاح الحقل * (بالإنجليزية)</label>
                    <input type="text" value={requirementForm.field_key} onChange={e => setRequirementForm({...requirementForm, field_key: e.target.value.toLowerCase().replace(/\s/g, '_')})} required disabled={!!editingRequirement} placeholder="مثال: plate_number" />
                  </div>
                  <div className="form-group">
                    <label>نوع الحقل *</label>
                    <select value={requirementForm.field_type} onChange={e => setRequirementForm({...requirementForm, field_type: e.target.value})} required>
                      {fieldTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>الاسم بالعربية *</label>
                    <input type="text" value={requirementForm.field_name_ar} onChange={e => setRequirementForm({...requirementForm, field_name_ar: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>الاسم بالإنجليزية</label>
                    <input type="text" value={requirementForm.field_name_en} onChange={e => setRequirementForm({...requirementForm, field_name_en: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>المجموعة</label>
                    <select value={requirementForm.group_name} onChange={e => setRequirementForm({...requirementForm, group_name: e.target.value})}>
                      <option value="">بدون مجموعة</option>
                      {groupOptions.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>الترتيب</label>
                    <input type="number" value={requirementForm.sort_order} onChange={e => setRequirementForm({...requirementForm, sort_order: parseInt(e.target.value) || 0})} />
                  </div>
                  <div className="form-group">
                    <label>نص توضيحي</label>
                    <input type="text" value={requirementForm.placeholder} onChange={e => setRequirementForm({...requirementForm, placeholder: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>القيمة الافتراضية</label>
                    <input type="text" value={requirementForm.default_value} onChange={e => setRequirementForm({...requirementForm, default_value: e.target.value})} />
                  </div>
                  
                  {requirementForm.field_type === 'select' && (
                    <div className="form-group full-width">
                      <label>الخيارات (مفصولة بفاصلة)</label>
                      <input type="text" value={requirementForm.options} onChange={e => setRequirementForm({...requirementForm, options: e.target.value})} placeholder="مثال: diesel, petrol, electric" />
                    </div>
                  )}
                  
                  {requirementForm.field_type === 'file' && (
                    <>
                      <div className="form-group">
                        <label>أنواع الملفات</label>
                        <input type="text" value={requirementForm.file_types} onChange={e => setRequirementForm({...requirementForm, file_types: e.target.value})} placeholder="مثال: pdf,jpg,png" />
                      </div>
                      <div className="form-group">
                        <label>الحجم الأقصى (KB)</label>
                        <input type="number" value={requirementForm.max_file_size} onChange={e => setRequirementForm({...requirementForm, max_file_size: e.target.value})} />
                      </div>
                    </>
                  )}
                  
                  <div className="form-group">
                    <label>تعبير التحقق (Regex)</label>
                    <input type="text" value={requirementForm.validation_regex} onChange={e => setRequirementForm({...requirementForm, validation_regex: e.target.value})} dir="ltr" />
                  </div>
                  <div className="form-group">
                    <label>رسالة خطأ التحقق</label>
                    <input type="text" value={requirementForm.validation_message} onChange={e => setRequirementForm({...requirementForm, validation_message: e.target.value})} />
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={requirementForm.is_required} onChange={e => setRequirementForm({...requirementForm, is_required: e.target.checked})} />
                      حقل إلزامي
                    </label>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input type="checkbox" checked={requirementForm.needs_verification} onChange={e => setRequirementForm({...requirementForm, needs_verification: e.target.checked})} />
                      يحتاج مراجعة من الإدارة
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowRequirementModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">حفظ</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .vehicle-settings-page { padding: 1.5rem; }
        .page-header { margin-bottom: 1.5rem; }
        .page-header__title h1 { margin: 0; }
        .page-header__title p { margin: 0.25rem 0 0; color: var(--tenant-text-secondary); }
        
        .stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 1.5rem; }
        .stat-card { background: var(--tenant-bg-secondary); padding: 1rem; border-radius: 8px; text-align: center; }
        .stat-value { display: block; font-size: 1.5rem; font-weight: 700; color: var(--tenant-primary); }
        .stat-label { font-size: 0.875rem; color: var(--tenant-text-secondary); }
        
        .tabs { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; }
        .tab { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; border: none;
          background: var(--tenant-bg-secondary); color: var(--tenant-text-secondary); border-radius: 8px;
          cursor: pointer; transition: all 0.2s; }
        .tab svg { width: 18px; height: 18px; }
        .tab:hover { background: var(--tenant-bg); }
        .tab.active { background: var(--tenant-primary); color: white; }
        
        .tab-content { background: var(--tenant-bg-secondary); border-radius: 12px; padding: 1.5rem; }
        .section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
        .section-header h3 { margin: 0; }
        
        .cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 1rem; }
        .card { background: var(--tenant-bg); border-radius: 8px; border: 1px solid var(--tenant-border); overflow: hidden; }
        .card.inactive { opacity: 0.6; }
        .card-header { padding: 1rem; border-bottom: 1px solid var(--tenant-border); display: flex; justify-content: space-between; align-items: center; }
        .card-header h4 { margin: 0; }
        .card-body { padding: 1rem; }
        .card-body p { margin: 0.25rem 0; font-size: 0.875rem; color: var(--tenant-text-secondary); }
        .card-actions { padding: 0.75rem 1rem; border-top: 1px solid var(--tenant-border); display: flex; gap: 0.5rem; }
        .card-actions button { padding: 0.5rem; border: none; background: transparent; cursor: pointer; border-radius: 4px; }
        .card-actions button:hover { background: var(--tenant-bg-secondary); }
        .card-actions button.danger:hover { background: rgba(239, 68, 68, 0.1); color: var(--tenant-danger); }
        .card-actions button svg { width: 16px; height: 16px; }
        
        .requirements-table { overflow-x: auto; }
        .requirements-table table { width: 100%; border-collapse: collapse; }
        .requirements-table th, .requirements-table td { padding: 0.75rem; text-align: right; border-bottom: 1px solid var(--tenant-border); }
        .requirements-table th { background: var(--tenant-bg); font-weight: 600; }
        .requirements-table tr.inactive { opacity: 0.5; }
        .requirements-table td strong { display: block; }
        .requirements-table td small { color: var(--tenant-text-secondary); font-size: 0.75rem; }
        
        .toggle-btn { padding: 0.25rem 0.75rem; border: none; border-radius: 12px; font-size: 0.75rem; cursor: pointer;
          background: var(--tenant-bg); color: var(--tenant-text-secondary); }
        .toggle-btn.active { background: var(--tenant-success); color: white; }
        
        .actions { display: flex; gap: 0.25rem; }
        .actions button { padding: 0.5rem; border: none; background: transparent; cursor: pointer; border-radius: 4px; }
        .actions button:hover { background: var(--tenant-bg-secondary); }
        .actions button.danger:hover { color: var(--tenant-danger); }
        .actions button svg { width: 16px; height: 16px; }
        
        .modal--lg { max-width: 700px; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        .form-group.full-width { grid-column: span 2; }
        .checkbox-group label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
        .checkbox-group input { width: auto; }
        
        @media (max-width: 768px) {
          .stats-row { grid-template-columns: repeat(2, 1fr); }
          .form-grid { grid-template-columns: 1fr; }
          .form-group.full-width { grid-column: span 1; }
        }
      `}</style>
    </div>
  );
};

export default VehicleSettingsPage;
